/**
 * Workflow Validation Engine
 *
 * Validates a Workflow DAG for:
 * 1. Cycle detection via Kahn's algorithm (topological sort)
 * 2. Type compatibility across all edges
 * 3. Precondition propagation through the DAG
 * 4. Parallel group detection (nodes at same depth)
 * 5. Conditional edge validation
 *
 * @module gibbs/workflow/validation
 */

import { getAction, isTypeCompatible } from '../registry/index';

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} valid - True if no errors were found
 * @property {string[]} errors - Human-readable error descriptions
 * @property {string[]} warnings - Non-fatal warnings
 * @property {string[]} topologicalOrder - Node IDs in valid execution order
 * @property {string[][]} parallelGroups - Groups of node IDs that can run concurrently
 * @property {Array<{nodeId: string, check: string, description: string}>} unsatisfiedPreconditions
 */

/**
 * Validate a workflow DAG.
 *
 * Runs cycle detection, type checking, precondition propagation, and
 * parallel group detection. Returns a structured result with errors,
 * warnings, and computed metadata.
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @returns {ValidationResult}
 */
export function validate(workflow) {
    const errors = [];
    const warnings = [];

    const nodes = workflow.getNodes();
    const edges = workflow.getEdges();

    // Handle empty workflow
    if (nodes.length === 0) {
        return {
            valid: true,
            errors: [],
            warnings: [],
            topologicalOrder: [],
            parallelGroups: [],
            unsatisfiedPreconditions: [],
        };
    }

    // --- Pass 1: Cycle detection via Kahn's algorithm ---
    const topologicalOrder = kahnTopologicalSort(workflow, errors);
    const hasCycle = topologicalOrder.length !== nodes.length;

    // --- Pass 2: Type compatibility for every edge ---
    checkTypeCompatibility(workflow, edges, errors);

    // --- Pass 3: Conditional edge validation ---
    checkConditionalEdges(workflow, nodes, warnings);

    // --- Pass 4: Precondition propagation (only if no cycles) ---
    let unsatisfiedPreconditions = [];
    if (!hasCycle) {
        unsatisfiedPreconditions = propagatePreconditions(workflow, topologicalOrder);
    }

    // --- Pass 5: Parallel group detection (only if no cycles) ---
    let parallelGroups = [];
    if (!hasCycle) {
        parallelGroups = computeParallelGroups(workflow, topologicalOrder);
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
        topologicalOrder,
        parallelGroups,
        unsatisfiedPreconditions,
    };
}

/**
 * Perform a topological sort using Kahn's algorithm.
 *
 * If the graph contains a cycle, the returned order will have fewer nodes
 * than the workflow, and an error is appended.
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @param {string[]} errors - Mutable array to append errors
 * @returns {string[]} Topological order of node IDs (partial if cycle exists)
 */
function kahnTopologicalSort(workflow, errors) {
    const nodes = workflow.getNodes();

    // Build in-degree map
    /** @type {Map<string, number>} */
    const inDegree = new Map();
    for (const node of nodes) {
        inDegree.set(node.id, 0);
    }
    for (const edge of workflow.getEdges()) {
        // Detect self-loops
        if (edge.sourceNodeId === edge.targetNodeId) {
            errors.push(
                `Self-loop detected: node "${edge.sourceNodeId}" has an edge to itself (edge "${edge.id}").`
            );
        }
        inDegree.set(edge.targetNodeId, (inDegree.get(edge.targetNodeId) || 0) + 1);
    }

    // Initialize queue with nodes that have zero in-degree
    const queue = [];
    for (const [nodeId, degree] of inDegree) {
        if (degree === 0) {
            queue.push(nodeId);
        }
    }

    const order = [];
    while (queue.length > 0) {
        // Sort for deterministic output
        queue.sort();
        const nodeId = queue.shift();
        order.push(nodeId);

        for (const edge of workflow.getOutgoingEdges(nodeId)) {
            const target = edge.targetNodeId;
            const newDegree = inDegree.get(target) - 1;
            inDegree.set(target, newDegree);
            if (newDegree === 0) {
                queue.push(target);
            }
        }
    }

    if (order.length !== nodes.length) {
        // Find nodes involved in cycle(s)
        const inCycle = nodes
            .filter((n) => !order.includes(n.id))
            .map((n) => n.id);
        errors.push(
            `Cycle detected: the following nodes are involved in a cycle: ${inCycle.join(', ')}.`
        );
    }

    return order;
}

/**
 * Check type compatibility for every edge in the workflow.
 *
 * Verifies that:
 * - The source action's output named by edge.outputName exists
 * - The target action's input named by edge.inputName exists
 * - The output type is compatible with the input type
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @param {import('./Workflow.js').WorkflowEdge[]} edges
 * @param {string[]} errors - Mutable array to append errors
 */
function checkTypeCompatibility(workflow, edges, errors) {
    for (const edge of edges) {
        const sourceNode = workflow.getNode(edge.sourceNodeId);
        const targetNode = workflow.getNode(edge.targetNodeId);
        if (!sourceNode || !targetNode) continue;

        const sourceAction = getAction(sourceNode.actionId);
        const targetAction = getAction(targetNode.actionId);

        if (!sourceAction) {
            errors.push(
                `Edge "${edge.id}": source node "${edge.sourceNodeId}" references unknown action "${sourceNode.actionId}".`
            );
            continue;
        }
        if (!targetAction) {
            errors.push(
                `Edge "${edge.id}": target node "${edge.targetNodeId}" references unknown action "${targetNode.actionId}".`
            );
            continue;
        }

        // Check outputName exists on source action
        const outputDef = sourceAction.outputs.find((o) => o.name === edge.outputName);
        if (!outputDef) {
            errors.push(
                `Edge "${edge.id}": output "${edge.outputName}" not found on source action "${sourceAction.id}" (node "${edge.sourceNodeId}"). `
                + `Available outputs: ${sourceAction.outputs.map((o) => o.name).join(', ') || 'none'}.`
            );
            continue;
        }

        // Check inputName exists on target action
        const inputDef = targetAction.inputs.find((i) => i.name === edge.inputName);
        if (!inputDef) {
            errors.push(
                `Edge "${edge.id}": input "${edge.inputName}" not found on target action "${targetAction.id}" (node "${edge.targetNodeId}"). `
                + `Available inputs: ${targetAction.inputs.map((i) => i.name).join(', ') || 'none'}.`
            );
            continue;
        }

        // Check type compatibility
        if (!isTypeCompatible(outputDef.type, inputDef.type)) {
            errors.push(
                `Edge "${edge.id}": type mismatch — output "${edge.outputName}" produces "${outputDef.type}" but input "${edge.inputName}" expects "${inputDef.type}" (not compatible).`
            );
        }
    }
}

/**
 * Validate conditional edges.
 *
 * Warns when a node has conditional outgoing edges but no unconditional
 * (default/else) path, meaning the workflow could stall if no condition
 * evaluates to true.
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @param {import('./Workflow.js').WorkflowNode[]} nodes
 * @param {string[]} warnings - Mutable array to append warnings
 */
function checkConditionalEdges(workflow, nodes, warnings) {
    for (const node of nodes) {
        const outgoing = workflow.getOutgoingEdges(node.id);
        if (outgoing.length === 0) continue;

        const conditionalEdges = outgoing.filter((e) => e.condition !== null);
        if (conditionalEdges.length === 0) continue;

        // Validate condition object structure
        for (const edge of conditionalEdges) {
            const cond = edge.condition;
            if (typeof cond !== 'object') {
                warnings.push(
                    `Edge "${edge.id}" has a non-object condition (got ${typeof cond}). `
                    + 'Conditions should be objects with at least a "description" property.'
                );
            } else if (!cond.description || typeof cond.description !== 'string') {
                warnings.push(
                    `Edge "${edge.id}" condition is missing a "description" string.`
                );
            }
        }

        const unconditionalEdges = outgoing.filter((e) => e.condition === null);
        if (unconditionalEdges.length === 0) {
            warnings.push(
                `Node "${node.id}" (${node.actionId}) has ${conditionalEdges.length} conditional outgoing edge(s) but no unconditional (default) path. `
                + 'The workflow may stall if no condition is met.'
            );
        }
    }
}

/**
 * Propagate preconditions through the DAG in topological order.
 *
 * For each node, accumulates postcondition guarantees from all upstream
 * nodes, then checks which of the node's own preconditions are not
 * satisfied by any upstream guarantee.
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @param {string[]} topologicalOrder - Node IDs in topological order
 * @returns {Array<{nodeId: string, check: string, description: string}>}
 */
function propagatePreconditions(workflow, topologicalOrder) {
    const unsatisfied = [];

    // Map nodeId -> Set of guarantee strings available at that node
    /** @type {Map<string, Set<string>>} */
    const guaranteesAtNode = new Map();

    for (const nodeId of topologicalOrder) {
        const node = workflow.getNode(nodeId);
        const actionDef = getAction(node.actionId);
        if (!actionDef) continue;

        // Accumulate guarantees from all upstream nodes
        const upstreamGuarantees = new Set();
        const incomingEdges = workflow.getIncomingEdges(nodeId);
        for (const edge of incomingEdges) {
            const sourceGuarantees = guaranteesAtNode.get(edge.sourceNodeId);
            if (sourceGuarantees) {
                for (const g of sourceGuarantees) {
                    upstreamGuarantees.add(g);
                }
            }
        }

        // Check this node's preconditions against upstream guarantees
        if (actionDef.preconditions) {
            for (const pre of actionDef.preconditions) {
                if (!upstreamGuarantees.has(pre.check)) {
                    unsatisfied.push({
                        nodeId,
                        check: pre.check,
                        description: pre.description,
                    });
                }
            }
        }

        // Compute guarantees available after this node:
        // upstream guarantees + this node's own postconditions
        const nodeGuarantees = new Set(upstreamGuarantees);
        if (actionDef.postconditions) {
            for (const post of actionDef.postconditions) {
                nodeGuarantees.add(post.guarantee);
            }
        }
        guaranteesAtNode.set(nodeId, nodeGuarantees);
    }

    return unsatisfied;
}

/**
 * Compute parallel execution groups by assigning each node a depth
 * (longest path from any root) and grouping nodes at the same depth.
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @param {string[]} topologicalOrder - Node IDs in topological order
 * @returns {string[][]} Groups of node IDs ordered by depth
 */
function computeParallelGroups(workflow, topologicalOrder) {
    /** @type {Map<string, number>} */
    const depth = new Map();

    // Initialize all nodes at depth 0
    for (const nodeId of topologicalOrder) {
        depth.set(nodeId, 0);
    }

    // Walk in topological order, computing longest path from root
    for (const nodeId of topologicalOrder) {
        const currentDepth = depth.get(nodeId);
        for (const edge of workflow.getOutgoingEdges(nodeId)) {
            const targetDepth = depth.get(edge.targetNodeId) || 0;
            if (currentDepth + 1 > targetDepth) {
                depth.set(edge.targetNodeId, currentDepth + 1);
            }
        }
    }

    // Group nodes by depth
    const maxDepth = Math.max(...Array.from(depth.values()), -1);
    const groups = [];
    for (let d = 0; d <= maxDepth; d++) {
        const group = topologicalOrder.filter((id) => depth.get(id) === d);
        if (group.length > 0) {
            groups.push(group);
        }
    }

    return groups;
}
